import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Section } from '@dized/ui';
import { shouldShowProgressLabel } from '../common/config/analyticsConfig';

const ResponsiveTitle = styled.h3`
  font-size: clamp(1.25rem, 3vw, 1.5rem);
  margin: 0 0 20px 0;
  word-break: break-word;
  line-height: 1.3;
  
  @media (max-width: 480px) {
    margin: 0 0 15px 0;
  }
`;

const ChartContainer = styled.div`
  width: 100%;
  padding: 20px 0;
`;

const MilestoneItem = styled.div`
  margin-bottom: 20px;
`;

const MilestoneHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const MilestoneName = styled.span`
  font-weight: 500;
  color: #333;
  font-size: 1rem;
`;

const MilestoneValue = styled.span`
  font-weight: bold;
  color: #2196F3;
  font-size: 1.1rem;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 24px;
  background: #f0f0f0;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, ${props => props.color || '#4CAF50'}, ${props => props.color || '#66BB6A'});
  width: ${props => props.percentage}%;
  transition: width 0.5s ease;
  border-radius: 12px;
  position: relative;
`;

const ProgressBarLabel = styled.span`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: white;
  font-size: 0.85rem;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
`;

const MilestoneDetails = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background: rgba(0,0,0,0.02);
  border-radius: 6px;
  font-size: 0.85rem;
  color: #666;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  font-weight: 500;
`;

const DetailValue = styled.span`
  color: #333;
  font-weight: 600;
`;

const formatTotalTimePlayed = (seconds) => {
  if (!seconds && seconds !== 0) return 'N/A';
  
  const totalSeconds = Math.round(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${remainingSeconds}s`;
};

const MilestoneGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const UserMilestones = ({ data }) => {
  if (!data) {
    return (
      <Section>
        <ResponsiveTitle>Progress Milestones</ResponsiveTitle>
        <p>No milestone data available</p>
      </Section>
    );
  }

  const milestones = [
    {
      key: 'intro',
      name: 'Intro Completed',
      percentage: data.state_intro_completed_percentage || 0,
      medianTime: data.state_intro_median_time_played,
      medianBlocks: data.state_intro_median_blocks_advanced,
      color: '#00BCD4'
    },
    {
      key: 'setup',
      name: 'Setup Completed',
      percentage: data.state_setup_completed_percentage || 0,
      medianTime: data.state_setup_median_time_played,
      medianBlocks: data.state_setup_median_blocks_advanced,
      color: '#4CAF50'
    },
    {
      key: 'firstturn',
      name: 'First Turn Complete',
      percentage: data.state_firstturn_completed_percentage || 0,
      medianTime: data.state_firstturn_median_time_played,
      medianBlocks: data.state_firstturn_median_blocks_advanced,
      color: '#F44336'
    },
    {
      key: 'teaching',
      name: 'Teaching Complete',
      percentage: data.state_teaching_completed_percentage || 0,
      medianTime: data.state_teaching_median_time_played,
      medianBlocks: data.state_teaching_median_blocks_advanced,
      color: '#2196F3'
    },
    {
      key: 'gameplay',
      name: 'Game End',
      percentage: data.state_gameplay_completed_percentage || 0,
      medianTime: data.state_gameplay_median_time_played,
      medianBlocks: data.state_gameplay_median_blocks_advanced,
      color: '#FF9800'
    },
    {
      key: 'tutorial',
      name: 'Tutorial Completed',
      percentage: data.state_tutorial_completed_percentage || 0,
      medianTime: data.state_tutorial_median_time_played,
      medianBlocks: data.state_tutorial_median_blocks_advanced,
      color: '#9C27B0'
    }
  ];

  // Sort by percentage for better visualization
  const sortedMilestones = milestones.sort((a, b) => b.percentage - a.percentage);

  return (
    <Section>
      <ResponsiveTitle>Progress Milestones</ResponsiveTitle>
      
      <ChartContainer>
        <MilestoneGrid>
          {sortedMilestones.map((milestone, index) => (
            <MilestoneItem key={milestone.key}>
              <MilestoneHeader>
                <MilestoneName>{milestone.name}</MilestoneName>
                <MilestoneValue>{milestone.percentage.toFixed(1)}%</MilestoneValue>
              </MilestoneHeader>
              <ProgressBarContainer>
                <ProgressBarFill 
                  percentage={milestone.percentage} 
                  color={milestone.color}
                >
                  {shouldShowProgressLabel(milestone.percentage) && (
                    <ProgressBarLabel>
                      {milestone.percentage.toFixed(1)}%
                    </ProgressBarLabel>
                  )}
                </ProgressBarFill>
              </ProgressBarContainer>
              <MilestoneDetails>
                <DetailRow>
                  <DetailLabel>Median Time:</DetailLabel>
                  <DetailValue>{formatTotalTimePlayed(milestone.medianTime)}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Median Blocks:</DetailLabel>
                  <DetailValue>{milestone.medianBlocks ? milestone.medianBlocks.toFixed(1) : 'N/A'}</DetailValue>
                </DetailRow>
              </MilestoneDetails>
            </MilestoneItem>
          ))}
        </MilestoneGrid>
      </ChartContainer>
    </Section>
  );
};

UserMilestones.propTypes = {
  data: PropTypes.shape({
    // Completion percentages
    state_intro_completed_percentage: PropTypes.number,
    state_setup_completed_percentage: PropTypes.number,
    state_tutorial_completed_percentage: PropTypes.number,
    state_firstturn_completed_percentage: PropTypes.number,
    state_teaching_completed_percentage: PropTypes.number,
    state_gameplay_completed_percentage: PropTypes.number,
    // Median time played
    state_intro_median_time_played: PropTypes.number,
    state_setup_median_time_played: PropTypes.number,
    state_tutorial_median_time_played: PropTypes.number,
    state_firstturn_median_time_played: PropTypes.number,
    state_teaching_median_time_played: PropTypes.number,
    state_gameplay_median_time_played: PropTypes.number,
    // Median blocks advanced
    state_intro_median_blocks_advanced: PropTypes.number,
    state_setup_median_blocks_advanced: PropTypes.number,
    state_tutorial_median_blocks_advanced: PropTypes.number,
    state_firstturn_median_blocks_advanced: PropTypes.number,
    state_teaching_median_blocks_advanced: PropTypes.number,
    state_gameplay_median_blocks_advanced: PropTypes.number
  })
};

UserMilestones.displayName = 'UserMilestones';
